import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Notify } from 'notiflix';
import moment from 'moment';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  Chip,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Info as InfoIcon,
  MonetizationOn as MonetizationOnIcon,
  Language as LanguageIcon,
  CalendarToday as CalendarIcon,
  Settings as SettingsIcon,
  Policy as PolicyIcon,
  AttachMoney as AttachMoneyIcon,
  Discount as DiscountIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { subscriptionApi } from 'api/subscriptionApi';
import { SubscriptionTypes, SubscriptionTypeLabels, SubscriptionTypeDescriptions, SubscriptionPolicies } from 'constants/subscriptionTypes';
import usePermission from 'hooks/usePermission';

const SubscriptionTypeView = () => {
  const navigate = useNavigate();
  const canUpdate = usePermission('shop-management', 'update');
  const { id } = useParams();
  
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // console.log("subscription : ", subscription);

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await subscriptionApi.getSubscriptionById(id);
      // console.log('Fetched subscription details:', data);
      setSubscription(data);
    } catch (err) {
      console.error('Error fetching subscription details:', err);
      setError(err.message || 'Failed to load subscription configuration');
      Notify.failure(err.message || 'Failed to load subscription configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchSubscriptionDetails();
    }
  }, [id]);

  const formatSubscriptionType = (type) => {
    if (!type) return 'N/A';
    
    const typeLower = type.toLowerCase();
    const label = SubscriptionTypeLabels[type] || SubscriptionTypeLabels[typeLower];
    
    if (label) {
      return {
        displayName: label,
        originalName: type,
        description: SubscriptionTypeDescriptions[type] || SubscriptionTypeDescriptions[typeLower] || ''
      };
    }
    
    const formattedName = type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return {
      displayName: formattedName,
      originalName: type,
      description: ''
    };
  };

  const getEntityName = () => {
    if (!subscription) return null;
    // console.log("subscription : ", subscription);

    // Agar entity_specific configuration hai
    if (subscription.configType === 'entity_specific' && subscription.targetEntityId) {
        // Try to get name from pricing rules
        if (subscription.pricing?.discountRules?.length > 0) {
        const matchingRule = subscription.pricing.discountRules.find(
            rule => rule.targetEntityId === subscription.targetEntityId
        );
        
        if (matchingRule?.overrideName) {
            // Extract entity name from overrideName
            // Example: "Custom pricing for App Development Essentials" → "App Development Essentials"
            const name = matchingRule.overrideName.replace('Custom pricing for ', '');
            return name;
        }
        }
        
        // Agar custom pricing nahi hai, to entity ID hi dikhayein
        return `Entity ID: ${subscription.targetEntityId.substring(0, 8)}...`;
    }

    return null;
  };

  const getEntityTypeDisplay = () => {
    if (!subscription?.targetEntityType) return null;

    const entityTypes = {
        valuepack: 'Value Pack',
        product: 'Product',
        service: 'Service',
        saas: 'SaaS'
    };

    return entityTypes[subscription.targetEntityType.toLowerCase()] || 
        subscription.targetEntityType.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  };

  const formatModuleType = (moduleType) => {
    if (!moduleType) return 'N/A';
    
    const moduleTypes = {
      valuepack: 'Value Pack',
      product: 'Product',
      service: 'Service',
      saas: 'SaaS'
    };
    
    return moduleTypes[moduleType.toLowerCase()] || 
      moduleType.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    
    if (typeof duration === 'object') {
      return duration.name || `${duration.value || ''} ${duration.unit || ''}`.trim() || 'N/A';
    }
    
    return duration;
  };

  const formatCountry = (country) => {
    if (!country) return 'All';
    
    if (typeof country === 'object') {
      return country.name || country.countryCode || 'All';
    }
    
    return country;
  };

  const getStatusChip = (subscription) => {
    const isActive = subscription.isActive !== false;
    
    return (
      <Chip 
        label={isActive ? 'Active' : 'Inactive'}
        size="small"
        color={isActive ? 'success' : 'error'}
        variant={isActive ? 'filled' : 'outlined'}
      />
    );
  };

  const getConfigTypeChip = (configType, targetEntityType) => {
    if (!configType) return null;
    
    const configTypeColors = {
      global: { color: 'primary', bg: '#e3f2fd' },
      entity_specific: { color: 'secondary', bg: '#f3e5f5' }
    };
    
    const colorInfo = configTypeColors[configType] || { color: 'default', bg: '#f5f5f5' };
    const label = configType === 'entity_specific' 
      ? `Entity Specific (${targetEntityType || 'N/A'})` 
      : 'Global';
    
    return (
      <Chip 
        label={label}
        size="small"
        sx={{ 
          backgroundColor: colorInfo.bg,
          color: colorInfo.color === 'primary' ? '#1976d2' : 
                 colorInfo.color === 'secondary' ? '#9c27b0' : '#616161',
          fontWeight: 500,
          fontSize: '0.75rem',
          height: '24px'
        }}
      />
    );
  };

  const getPricingStrategyChip = (strategy) => {
    if (!strategy) return null;
    
    const strategyColors = {
      global: { color: 'primary', bg: '#e3f2fd' },
      custom: { color: 'secondary', bg: '#f3e5f5' }
    };
    
    const colorInfo = strategyColors[strategy] || { color: 'default', bg: '#f5f5f5' };
    
    return (
      <Chip 
        label={strategy.charAt(0).toUpperCase() + strategy.slice(1)}
        size="small"
        sx={{ 
          backgroundColor: colorInfo.bg,
          color: colorInfo.color === 'primary' ? '#1976d2' : 
                 colorInfo.color === 'secondary' ? '#9c27b0' : '#616161',
          fontWeight: 500,
          fontSize: '0.75rem',
          height: '24px'
        }}
      />
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Box textAlign="center">
            <CircularProgress />
            <Typography variant="h6" mt={2}>Loading subscription configuration...</Typography>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" flexDirection="column" alignItems="center">
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/admin/subscription-type')}
            startIcon={<ArrowBackIcon />}
          >
            Back to Subscription List
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  if (!subscription) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" flexDirection="column" alignItems="center">
          <Typography variant="h6" gutterBottom>Subscription configuration not found</Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/admin/subscription-type')}
            startIcon={<ArrowBackIcon />}
          >
            Back to Subscription List
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  const typeInfo = formatSubscriptionType(subscription.subscriptionType);
  const policyInfo = SubscriptionPolicies[subscription.subscriptionType] || {};

  const renderBasicInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  {typeInfo.displayName}
                  <Typography component="span" ml={2}>
                    {getStatusChip(subscription)}
                  </Typography>
                </Typography>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  {typeInfo.description}
                </Typography>
              </Box>
              {subscription.framework && (
                <Chip
                  label={`Framework: ${subscription.framework.name}`}
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box mb={3}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    <CategoryIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Module Type
                  </Typography>
                  <Typography variant="h6">
                    {formatModuleType(subscription.moduleType)}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    <CalendarIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Duration
                  </Typography>
                  <Typography variant="h6">
                    {formatDuration(subscription.duration)}
                    {subscription.duration?.value && subscription.duration?.unit && (
                      <Typography variant="body2" color="textSecondary">
                        ({subscription.duration.value} {subscription.duration.unit})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box mb={3}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    <LanguageIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Country
                  </Typography>
                  <Typography variant="h6">
                    {formatCountry(subscription.country)}
                    {subscription.country?.currencySymbol && (
                      <Typography variant="body2" color="textSecondary">
                        Currency: {subscription.country.currencySymbol}
                      </Typography>
                    )}
                  </Typography>
                </Box>

                <Box mb={3}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    <SettingsIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Configuration Type
                  </Typography>
                  <Box>
                    {getConfigTypeChip(subscription.configType, subscription.targetEntityType)}
                    {subscription.targetEntityId && (
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Entity ID: {subscription.targetEntityId}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Original Subscription Type
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 3 }}>
              <Typography variant="body1" fontFamily="monospace">
                {typeInfo.originalName}
              </Typography>
            </Box>

            {/* {subscription.targetEntityType && subscription.targetEntityId && (
              <>
                <Typography variant="h6" gutterBottom>
                  Target Entity Details
                </Typography>
                <Alert severity="info">
                  This configuration is specific to: <strong>{subscription.targetEntityType}</strong> 
                  (ID: {subscription.targetEntityId})
                </Alert>
              </>
            )} */}

            {subscription.targetEntityType && subscription.targetEntityId && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Target Entity Details
                </Typography>
                    
                <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                    <CardContent>
                        <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Box>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Entity Type
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                                {getEntityTypeDisplay()}
                            </Typography>
                            </Box>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Box>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Entity Name
                            </Typography>
                            <Typography variant="body1" fontWeight="medium" color="primary">
                                {getEntityName() || 'Name not available'}
                            </Typography>
                            </Box>
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="textSecondary">
                                Entity ID: {subscription.targetEntityId}
                            </Typography>
                            </Box>
                        </Grid>
                        </Grid>
                        
                        {/* Additional info if available */}
                        {subscription.pricing?.strategy === 'custom' && subscription.pricing?.discountRules?.length > 0 && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                Custom pricing configured for this entity
                                {subscription.pricing.discountRules[0]?.discountPercentage && 
                                    ` with ${parseFloat(subscription.pricing.discountRules[0].discountPercentage)}% discount`}
                                </Typography>
                            </Alert>
                        )}
                    </CardContent>
                </Card>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Metadata" />
          <CardContent>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Created At
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {moment(subscription.createdAt).format('MMM D, YYYY hh:mm A')}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Last Updated
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {moment(subscription.updatedAt).format('MMM D, YYYY hh:mm A')}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="textSecondary">
                Configuration ID
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {subscription.id}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderPricingDetails = () => (
    <Grid container spacing={3}>
        <Grid item xs={12}>
        <Card>
            <CardContent>
            <Box display="flex" alignItems="center" mb={3}>
                <MonetizationOnIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h5">
                Pricing Strategy
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Left Column - Basic Info */}
                <Grid item xs={12} md={4}>
                <Box mb={3}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Strategy
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                    {getPricingStrategyChip(subscription.pricing?.strategy)}
                    {subscription.pricing?.strategy === 'custom' && 
                    subscription.pricing?.discountRules?.length > 0 && (
                        <Chip 
                        label={`${subscription.pricing.discountRules.length} rules`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        />
                    )}
                    </Box>
                </Box>

                {subscription.pricing?.globalDiscount && 
                parseFloat(subscription.pricing.globalDiscount) > 0 && (
                    <Box mb={3}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        <DiscountIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Global Discount
                    </Typography>
                    <Typography variant="h4" color="success.main">
                        {parseFloat(subscription.pricing.globalDiscount)}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Applies to all items
                    </Typography>
                    </Box>
                )}
                </Grid>

                {/* Right Column - Rules Table */}
                <Grid item xs={12} md={8}>
                {subscription.pricing?.strategy === 'custom' && 
                subscription.pricing?.discountRules?.length > 0 ? (
                    <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom mb={2}>
                        Custom Pricing Rules ({subscription.pricing.discountRules.length})
                    </Typography>
                    <TableContainer 
                        component={Paper} 
                        variant="outlined"
                        sx={{ 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                        }}
                    >
                        <Table size="small">
                        <TableHead>
                            <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Entity</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Discount</TableCell>
                            {/* <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell> */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subscription.pricing.discountRules.map((rule, index) => (
                            <TableRow 
                                key={rule.id || index}
                                sx={{ 
                                '&:last-child td, &:last-child th': { border: 0 },
                                '&:hover': { backgroundColor: 'action.hover' }
                                }}
                            >
                                <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                    {rule.overrideName || `Entity ${rule.targetEntityId?.substring(0, 8)}...`}
                                </Typography>
                                </TableCell>
                                <TableCell>
                                <Chip 
                                    label={rule.targetEntityType} 
                                    size="small" 
                                    variant="outlined"
                                />
                                </TableCell>
                                <TableCell align="right">
                                <Typography 
                                    variant="body1" 
                                    color="success.main" 
                                    fontWeight="bold"
                                >
                                    {parseFloat(rule.discountPercentage)}%
                                </Typography>
                                </TableCell>
                                <TableCell>
                                {/* <Typography variant="body2" color="textSecondary">
                                    {rule.overrideDescription || 'Custom pricing rule'}
                                </Typography> */}
                                </TableCell>
                            </TableRow>
                            ))}
                        </TableBody>
                        </Table>
                    </TableContainer>
                    </Box>
                ) : subscription.pricing?.strategy === 'custom' ? (
                    <Box textAlign="center" py={4}>
                    <Typography variant="body1" color="textSecondary">
                        No custom pricing rules defined
                    </Typography>
                    </Box>
                ) : null}
                </Grid>
            </Grid>
            </CardContent>
        </Card>
        </Grid>
    </Grid>
  );

  const renderConfiguration = () => (
    <Grid container spacing={3}>
      {subscription.config && (
        <>
          {/* Cooling-Off Period */}
          {subscription.config.coolingOffPeriod && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Cooling-Off Period" />
                <CardContent>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color={subscription.config.coolingOffPeriod.enabled ? "success" : "error"} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Enabled" 
                        secondary={subscription.config.coolingOffPeriod.enabled ? 'Yes' : 'No'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Days" 
                        secondary={subscription.config.coolingOffPeriod.days || 'N/A'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Refund Percentage" 
                        secondary={`${subscription.config.coolingOffPeriod.refundPercentage || 0}%`} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Renewal Settings */}
          {subscription.config.renewal && subscription.subscriptionType !== SubscriptionTypes.ONE_TIME && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Renewal Settings" />
                <CardContent>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color={subscription.config.renewal.autoRenew ? "success" : "error"} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Auto Renew" 
                        secondary={subscription.config.renewal.autoRenew ? 'Yes' : 'No'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Grace Period" 
                        secondary={`${subscription.config.renewal.gracePeriodDays || 0} days`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Reminder Before" 
                        secondary={`${subscription.config.renewal.reminderDaysBefore || 0} days`} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Cancellation Policy */}
          {subscription.config.cancellation && subscription.subscriptionType === SubscriptionTypes.SUBSCRIPTION_CANCELLATION_ALLOWED && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Cancellation Policy" />
                <CardContent>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color={subscription.config.cancellation.allowed ? "success" : "error"} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Allowed" 
                        secondary={subscription.config.cancellation.allowed ? 'Yes' : 'No'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Allowed Within" 
                        secondary={`${subscription.config.cancellation.allowedWithinDays || 0} days`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Refund Percentage" 
                        secondary={`${subscription.config.cancellation.refundPercentage || 0}%`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Admin Approval Required" 
                        secondary={subscription.config.cancellation.adminApprovalRequired ? 'Yes' : 'No'} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Billing Configuration */}
          {subscription.config.billing && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Billing Configuration" />
                <CardContent>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color={subscription.config.billing.proRataRefund ? "success" : "error"} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Pro-Rata Refund" 
                        secondary={subscription.config.billing.proRataRefund ? 'Yes' : 'No'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Billing Cycles" 
                        secondary={subscription.config.billing.billingCycles === 0 ? 'Indefinite' : `${subscription.config.billing.billingCycles} cycles`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Trial Period" 
                        secondary={`${subscription.config.billing.trialPeriodDays || 0} days`} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );

  const renderPolicyInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={3}>
              <PolicyIcon color="info" sx={{ mr: 1, fontSize: 28 }} />
              <Typography variant="h5">
                Subscription Policy Details
              </Typography>
            </Box>

            {Object.keys(policyInfo).length > 0 ? (
              <Grid container spacing={3}>
                {/* Payment Information */}
                {policyInfo.payment && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardHeader 
                        title="Payment Details" 
                        titleTypographyProps={{ variant: 'h6' }}
                      />
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          Type: {policyInfo.payment.type || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" paragraph>
                          {policyInfo.payment.description || 'No description available'}
                        </Typography>
                        <Typography variant="subtitle2" gutterBottom>
                          Benefits:
                        </Typography>
                        <List dense>
                          {policyInfo.payment.benefits?.map((benefit, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={benefit} />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Refund Policy */}
                {policyInfo.refund && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardHeader 
                        title="Refund Policy" 
                        titleTypographyProps={{ variant: 'h6' }}
                      />
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          Allowed: {policyInfo.refund.allowed ? 'Yes' : 'No'}
                        </Typography>
                        {policyInfo.refund.period && (
                          <Typography variant="body2" color="textSecondary" paragraph>
                            Period: {policyInfo.refund.period}
                          </Typography>
                        )}
                        {policyInfo.refund.conditions && policyInfo.refund.conditions.length > 0 && (
                          <>
                            <Typography variant="subtitle2" gutterBottom>
                              Conditions:
                            </Typography>
                            <List dense>
                              {policyInfo.refund.conditions.map((condition, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <InfoIcon color="info" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={condition} />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Cancellation Policy */}
                {policyInfo.cancellation && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardHeader 
                        title="Cancellation Policy" 
                        titleTypographyProps={{ variant: 'h6' }}
                      />
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          Allowed: {policyInfo.cancellation.allowed ? 'Yes' : 'No'}
                        </Typography>
                        {policyInfo.cancellation.note && (
                          <Typography variant="body2" color="textSecondary" paragraph>
                            Note: {policyInfo.cancellation.note}
                          </Typography>
                        )}
                        {policyInfo.cancellation.conditions && policyInfo.cancellation.conditions.length > 0 && (
                          <>
                            <Typography variant="subtitle2" gutterBottom>
                              Conditions:
                            </Typography>
                            <List dense>
                              {policyInfo.cancellation.conditions.map((condition, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <InfoIcon color="info" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={condition} />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Access Information */}
                {policyInfo.access && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardHeader 
                        title="Access" 
                        titleTypographyProps={{ variant: 'h6' }}
                      />
                      <CardContent>
                        <Typography variant="body2" color="textSecondary">
                          {policyInfo.access}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Alert severity="info">
                No policy information available for this subscription type.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={() => navigate('/admin/subscription-type')}
                sx={{ color: '#fff', mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <MDTypography variant="h6" color="white">
                {typeInfo.displayName} - Configuration Details
              </MDTypography>
            </Box>

            <Box display="flex" gap={2}>
              {canUpdate && (
                <Button
                  variant="contained"
                  color="light"
                  onClick={() => navigate(`/admin/subscription-type/edit/${subscription.id}`)}
                  startIcon={<EditIcon />}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    }
                  }}
                >
                  Edit
                </Button>
              )}
              <Button
                variant="outlined"
                color="light"
                onClick={() => navigate('/admin/subscription-type')}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderColor: 'rgba(255,255,255,0.3)',
                  color: '#fff',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.5)',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                Back to List
              </Button>
            </Box>
          </MDBox>

          <MDBox p={3}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Basic Information" />
              <Tab label="Pricing Details" />
              <Tab label="Configuration" />
              <Tab label="Policy Information" />
            </Tabs>

            {activeTab === 0 && renderBasicInfo()}
            {activeTab === 1 && renderPricingDetails()}
            {activeTab === 2 && renderConfiguration()}
            {activeTab === 3 && renderPolicyInfo()}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default SubscriptionTypeView;
